import {Checkbox, TextField} from '@mui/material';
import {
    ActionType,
    ColumnData,
    ColumnFormats,
    ColumnTypes,
    FormatValueToDisplayProps,
    GetFormFieldProps,
    Row,
    RowColorMapping,
    setElementsOrderType,
    ValidValue,
} from './types';
import {DesktopDatePicker, DesktopDateTimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import styles from './NewTable.module.scss';
import TableCellTooltip from './TableCellTooltip';
import {cleanStringWithRegex, DictionaryItem, DT_FORMATS, formatDate, invokeAsync} from 'utils';
import {ErrorAdornment} from './components/ErrorAndornment';
import {TableAutocomplete} from './components/TableAutocomplete';
import {TableMultipleAutocomplete} from './components/TableMultipleAutocomplete';
import {formateStringAsNumber, formatNumber} from 'utils/functions/number';
import i18n from 'i18n';

export const formatValueToDisplay = ({value, column, rowId}: FormatValueToDisplayProps) => {
    if (column.customDisplayFormat) {
        return column.customDisplayFormat(rowId, value);
    }
    if (column.actionType === ActionType.DATE_TIME_SELECT_ONLY_TIME) {
        return value ? dayjs(value).format(DT_FORMATS.shortTime) : null;
    }
    if (column.format === ColumnFormats.DATE) {
        return value ? formatDate(value, DT_FORMATS.defaultDate) : null;
    }
    if (column.format === ColumnFormats.DATE_TIME_DEFAULT) {
        return value ? dayjs(value).format(DT_FORMATS.default) : null;
    }
    if (column.format === ColumnFormats.DATE_TIME) {
        return value ? formatDate(value) : null;
    }
    if (column.format === ColumnFormats.CURRENCY) {
        const parts = value.toString().split('.');
        parts[0] = formatNumber(parts[0]);
        return parts.join('.');
    }
    if (column.type === ColumnTypes.BOOLEAN) {
        return value === true ? i18n.t('yes') : value === false ? i18n.t('no') : '';
    }

    if (column.type === ColumnTypes.STRING) {
        return (
            <TableCellTooltip
                value={value}
                column={column}
            />
        );
    }

    if (column.type === ColumnTypes.ARRAY) {
        const stringValue = value && Array.isArray(value) ? value.join(', ') : '';
        return (
            <TableCellTooltip
                value={stringValue}
                column={column}
            />
        );
    }
    return value;
};

export const getTableField = ({
    valueKey,
    dictionary,
    values,
    setValue,
    column,
    disabled,
    CustomComponent,
    customProps,
    required = false,
    validationError,
}: GetFormFieldProps) => {
    const value = values[valueKey];
    if (CustomComponent) {
        return (
            <CustomComponent
                valueKey={valueKey}
                dictionary={dictionary}
                values={values}
                setValue={setValue}
                column={column}
                disabled={disabled}
                customProps={customProps}
                required={required}
            />
        );
    }

    if (column.type === ColumnTypes.ARRAY) {
        return (
            <TableMultipleAutocomplete
                disabled={disabled}
                dictionary={dictionary}
                valueKey={valueKey}
                setValue={setValue}
                required={required}
                values={values}
                column={column}
            />
        );
    }

    if (column.dictName) {
        return (
            <TableAutocomplete
                disabled={disabled}
                dictionary={dictionary}
                valueKey={valueKey}
                setValue={setValue}
                required={required}
                values={values}
                column={column}
            />
        );
    }

    if (column.type === ColumnTypes.BOOLEAN) {
        return (
            <Checkbox
                checked={value || false}
                onChange={(event) => setValue(valueKey, event.target.checked)}
                sx={{padding: '4px'}}
            />
        );
    }

    if (column.format === ColumnFormats.DATE || column.format === ColumnFormats.DATE_TIME_DEFAULT) {
        return (
            <DesktopDatePicker
                value={value ? dayjs(value) : null}
                format={DT_FORMATS.defaultDate}
                onChange={(newValue) =>
                    setValue(valueKey, formatDate(newValue, DT_FORMATS.utcDate))
                }
                className={styles.dataPicker}
                sx={{
                    minWidth: 160,
                    '.MuiInputBase-root': {
                        padding: 0,
                    },
                    '.MuiInputBase-input': {
                        padding: '6px 0',
                    },
                }}
                slotProps={{
                    textField: {
                        InputProps: {
                            startAdornment: (
                                <ErrorAdornment
                                    value={value}
                                    required={required}
                                />
                            ),
                        },
                    },
                }}
            />
        );
    }
    if (column.format === ColumnFormats.DATE_TIME) {
        return (
            <DesktopDateTimePicker
                ampm={false}
                format={DT_FORMATS.default}
                value={value ? dayjs(value) : null}
                className={styles.dataPicker}
                onChange={(newValue) =>
                    setValue(valueKey, formatDate(newValue, DT_FORMATS.utcFull))
                }
                sx={{
                    minWidth: 200,
                    '.MuiInputBase-root': {
                        padding: 0,
                    },
                    '.MuiInputBase-input': {
                        padding: '6px 0',
                    },
                }}
                slotProps={{
                    textField: {
                        InputProps: {
                            startAdornment: (
                                <ErrorAdornment
                                    value={value}
                                    required={required}
                                />
                            ),
                        },
                    },
                }}
            />
        );
    }
    if (column.type === ColumnTypes.OBJECT) {
        return <></>;
    }

    return (
        <TextField
            fullWidth
            maxRows={column.type === ColumnTypes.STRING ? 4 : 1}
            multiline={column.type === ColumnTypes.STRING}
            size='small'
            value={value || '' || (value === 0 ? value : '')}
            variant='standard'
            error={!!validationError}
            type={column.type === ColumnTypes.STRING ? 'string' : 'number'}
            style={{minWidth: column.type === ColumnTypes.STRING ? '150px' : '70px'}}
            InputProps={{
                startAdornment: (
                    <ErrorAdornment
                        value={value}
                        required={required}
                    />
                ),
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (column.type === ColumnTypes.INTEGER) {
                    const newValue = event.target.value;
                    const modifyValue = formateStringAsNumber({
                        value: newValue,
                        onlyPositive: false,
                    });
                    setValue(valueKey, modifyValue);
                    return;
                }
                if (column.type === ColumnTypes.NUMBER) {
                    const newValue = event.target.value;
                    const modifyValue = newValue.replace('e', '');
                    setValue(valueKey, modifyValue);
                    return;
                }
                const formattedValue = cleanStringWithRegex(
                    event.target.value,
                    column.allowedChars,
                );
                setValue(valueKey, formattedValue);
            }}
        />
    );
};

export const onSetElementsOrder =
    (setElementsOrder: setElementsOrderType, startIndex?: number) =>
    (orders: Array<string | number>) => {
        const newOrder = orders.reduce((res: Record<string | number, number>, item, index) => {
            res[item] = (startIndex || 0) + index + 1;
            return res;
        }, {});

        return invokeAsync(() => setElementsOrder(newOrder));
    };

export const handleSelectForTable = (
    id: number,
    selected: number[],
    setSelected: React.Dispatch<React.SetStateAction<number[]>>,
) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
            selected.slice(0, selectedIndex),
            selected.slice(selectedIndex + 1),
        );
    }
    setSelected(newSelected);
};

type getOptionsProps = {
    dictionary?: DictionaryItem[] | string | null;
    column: ColumnData;
    values: Record<string, string>;
};

export const getOptions = ({dictionary, column, values}: getOptionsProps): DictionaryItem[] => {
    if (Array.isArray(dictionary)) {
        if (column?.keyTableAttr && values?.[column.keyTableAttr]) {
            const keyTableAttr = column.keyTableAttr;
            return dictionary?.filter(
                (el) => el.key.toLowerCase() === values?.[keyTableAttr].toLowerCase(),
            );
        }
        return dictionary;
    }
    return [];
};

const validateStringValue = (value: string, column: ColumnData): string | null => {
    if (column.minLength && column.maxLength) {
        if (
            String(value).length < column.minLength ||
            String(value).length > column.maxLength ||
            String(value).length === 0
        ) {
            return `${i18n.t('validation.enterNumberOfCharacters', {ns: 'tables'})} ${i18n.t('pretext.from2', {ns: 'common'}).toLowerCase()} ${column.minLength} ${i18n.t('pretext.toDepth', {ns: 'common'}).toLowerCase()} ${column.maxLength}`;
        }
    } else {
        if (column.minLength && String(value).length < column.minLength) {
            return `${i18n.t('validation.numberOfCharactersMustBeAtLeast', {ns: 'tables'})} ${column.minLength}`;
        }
        if (column.maxLength && String(value).length > column.maxLength) {
            return `${i18n.t('validation.numberOfCharactersMustBeNoMoreThan', {ns: 'tables'})} ${column.maxLength}`;
        }
    }
    return null;
};

const validateNumberValue = (value: number, column: ColumnData): string | null => {
    if (!isNaN(value)) {
        if (column.minimum !== undefined && column.maximum !== undefined) {
            if (value < column.minimum || value > column.maximum) {
                return `${i18n.t('validation.enterValue', {ns: 'tables'})} ${i18n.t('pretext.from2', {ns: 'common'}).toLowerCase()} ${column.minimum} ${i18n.t('pretext.toDepth', {ns: 'common'}).toLowerCase()} ${column.maximum}`;
            }
        } else {
            if (column.minimum !== undefined && value < column.minimum) {
                return `${i18n.t('validation.minimumAcceptableValue', {ns: 'tables'})}: ${column.minimum}`;
            }
            if (column.maximum !== undefined && value > column.maximum) {
                return `${i18n.t('validation.maximumAcceptableValue', {ns: 'tables'})}: ${column.maximum}`;
            }
            if (column.exclusiveMinimum !== undefined && value <= column.exclusiveMinimum) {
                return i18n.t('validation.valueMustBePositive', {ns: 'tables'});
            }
            if (column.exclusiveMaximum !== undefined && value >= column.exclusiveMaximum) {
                return i18n.t('validation.valueMustBeNegative', {ns: 'tables'});
            }
        }
    }
    return null;
};

export const validateValue = (value: ValidValue, column: ColumnData): string | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (column.type === ColumnTypes.STRING) {
        return validateStringValue(String(value), column);
    }

    if (column.type === ColumnTypes.NUMBER || column.type === ColumnTypes.INTEGER) {
        return validateNumberValue(Number(value), column);
    }

    return null;
};

export const getRowBackgroundColor = <T extends Row>(
    row: T,
    rowColorMapping?: RowColorMapping<T>,
    editElement?: T | null,
): string | undefined => {
    if (editElement?.id === row.id) {
        return undefined;
    }

    if (!rowColorMapping) {
        return undefined;
    }

    if (rowColorMapping.activeFieldName) {
        const activeValue = row[rowColorMapping.activeFieldName];
        if (activeValue === false) {
            return rowColorMapping.defaultColor;
        }
    }

    const columnValue = row[rowColorMapping.columnId];

    if (columnValue !== undefined && columnValue !== null) {
        const color = rowColorMapping.valueColorMap[columnValue];
        if (color) {
            return color;
        }
    }

    return rowColorMapping.defaultColor;
};
